import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Field, TextInput } from "../../components/ui/Field";
import { Select } from "../../components/ui/Select";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { Space, SpaceMember } from "../../lib/api/models";
import { qk } from "../../lib/queries";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function MembersDialog({ space, open, onClose }: { space: Space; open: boolean; onClose: () => void }) {
  const { data: loaded } = useQuery({
    queryKey: qk.members(space.id),
    queryFn: () => api.spaces.members(space.id),
    enabled: open,
  });
  const { data: users = [] } = useQuery({ queryKey: qk.users, queryFn: api.users.list, enabled: open });
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [userId, setUserId] = useState("");
  const [group, setGroup] = useState("");
  const [groupError, setGroupError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loaded) setMembers(loaded);
  }, [loaded]);

  const addUser = () => {
    const u = users.find((x) => x.id === userId);
    if (!u || members.some((m) => m.principalType === "user" && m.principalId === u.id)) return;
    setMembers([...members, { principalType: "user", principalId: u.id, label: u.name }]);
    setUserId("");
  };
  const addGroup = () => {
    const id = group.trim().toLowerCase();
    if (!GUID.test(id)) {
      setGroupError("Paste the group's object ID from Entra (a GUID).");
      return;
    }
    if (!members.some((m) => m.principalType === "group" && m.principalId === id)) {
      setMembers([...members, { principalType: "group", principalId: id, label: `Group ${id}` }]);
    }
    setGroup("");
    setGroupError(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.spaces.setMembers(
        space.id,
        members.map(({ principalType, principalId }) => ({ principalType, principalId })),
      );
      toast(`Members of ${space.name} saved.`, "success");
      onClose();
    } catch (e) {
      toast(`Members could not be saved: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Members of ${space.name}`}
      description="Only members and admins can see a restricted topic or client, its documents, search hits and exports."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving}>
            Save members
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <ul className="grid gap-1">
          {members.length === 0 ? (
            <li className="text-sm text-ink-3">No members yet: only admins can see it.</li>
          ) : null}
          {members.map((m) => (
            <li
              key={`${m.principalType}:${m.principalId}`}
              className="flex items-center justify-between rounded-md bg-column px-3 py-1.5 text-sm"
            >
              <span>
                {m.label}{" "}
                <span className="text-xs text-ink-3">({m.principalType === "user" ? "user" : "Entra group"})</span>
              </span>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${m.label}`}
                onClick={() => setMembers(members.filter((x) => x !== m))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Add a person">
              {(id) => (
                <Select
                  id={id}
                  value={userId}
                  onChange={setUserId}
                  options={[
                    { value: "", label: "Choose…" },
                    ...users
                      .filter((u) => u.active && !u.disabled)
                      .map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
                  ]}
                />
              )}
            </Field>
          </div>
          <Button onClick={addUser} disabled={!userId}>
            Add person
          </Button>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Entra group object ID" error={groupError}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  invalid={Boolean(groupError)}
                  className="font-mono"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Button onClick={addGroup}>Add group</Button>
        </div>
      </div>
    </Dialog>
  );
}
